import React, { useMemo, useRef, useState } from 'react';

import { makePlaceholderDataUri, safeDimension } from '../lib/attachments';
import { thumbhashToDataURL } from '../lib/thumbdata';

import { computeImageFrameLayout, resolveIntrinsicImageDimensions } from '../feed/echo/attachmentGeometry';

export { computeImageFrameLayout };

/**
 * React component: thin view that applies the computed layout.
 * Props:
 *  - src, alt (required)
 *  - width, height (intrinsic dimensions; required)
 *  - mode: "single" | "group" (default "single")
 *  - previewSrc: optional sharp local preview shown until src renders
 *  - className, style (optional)
 *  - imgProps: pass-through props for <img> (e.g., ref, onClick)
 *  - children: optional content to display below the image
 */
export function ImageFrame({
  src,
  alt,
  width,
  height,
  mode = 'single',
  previewSrc,
  className,
  style,
  imgProps,
  thumbsize,
  thumbhash,
  children,
}) {
  const intrinsicDimensions = useMemo(() => resolveIntrinsicImageDimensions(width, height, thumbsize), [width, height, thumbsize]);
  const layout = useMemo(
    () => computeImageFrameLayout(intrinsicDimensions.width, intrinsicDimensions.height, mode),
    [intrinsicDimensions.height, intrinsicDimensions.width, mode],
  );
  const { finalFrameW, finalFrameH, finalImgW, finalImgH } = layout;
  const [renderedSrc, setRenderedSrc] = useState(null);
  const [failedSrc, setFailedSrc] = useState(null);
  const currentSrcRef = useRef(src);
  currentSrcRef.current = src;
  const placeholderWidth = safeDimension(finalFrameW ?? finalImgW ?? intrinsicDimensions.width, 1);
  const placeholderHeight = safeDimension(finalFrameH ?? finalImgH ?? intrinsicDimensions.height, 1);
  const placeholderSrc = useMemo(() => makePlaceholderDataUri(placeholderWidth, placeholderHeight), [placeholderHeight, placeholderWidth]);
  // Malformed thumbhash data can throw; fall back to plain placeholder.
  const thumbhashSrc = useMemo(() => {
    if (!thumbhash) return null;
    try {
      return thumbhashToDataURL(thumbhash);
    } catch {
      return null;
    }
  }, [thumbhash]);

  const { onLoad: forwardedOnLoad, onError: forwardedOnError, className: forwardedImgClassName, ...restImgProps } = imgProps || {};
  const isRendered = Boolean(src) && renderedSrc === src;
  const hasError = Boolean(src) && failedSrc === src;

  const handleImageLoad = (event) => {
    if (!src || currentSrcRef.current !== src) return;

    setRenderedSrc(src);
    setFailedSrc(null);

    if (typeof forwardedOnLoad === 'function') {
      forwardedOnLoad(event);
    }
  };

  const handleImageError = (event) => {
    if (!src || currentSrcRef.current !== src) return;

    setFailedSrc(src);
    setRenderedSrc(src);

    if (typeof forwardedOnError === 'function') {
      forwardedOnError(event);
    }
  };

  const hasThumbhash = Boolean(thumbhashSrc);
  const hasPreview = Boolean(previewSrc);
  const showingPreview = hasPreview && (hasError || !isRendered);
  const showingThumbhash = !showingPreview && hasThumbhash && (hasError || !isRendered);
  const displayedSrc = src && !hasError ? src : placeholderSrc;
  const isDisplayingSource = Boolean(src) && displayedSrc === src;
  const intrinsicWidth = safeDimension(finalImgW ?? intrinsicDimensions.width, placeholderWidth);
  const intrinsicHeight = safeDimension(finalImgH ?? intrinsicDimensions.height, placeholderHeight);
  // Thumbhash shows as a background on the figure; hide the gray SVG sizer.
  // Without a thumbhash, keep the SVG partially visible as a loading hint.
  // With a thumbhash, crossfade hash -> real image once the <img> actually renders.
  // If the source fails but thumbhash exists, keep hash-only fallback visible.
  const opacityClass =
    showingPreview || (hasError && showingThumbhash)
      ? 'opacity-0'
      : isRendered
        ? 'opacity-100'
        : showingThumbhash
          ? 'opacity-0'
          : 'opacity-50';
  // A sharp local preview already matches the uploaded image. Swap it for the
  // remote variant without a fade so fast processing cannot make message
  // acknowledgement look like an opacity transition.
  const transitionClass = hasThumbhash && !hasPreview ? 'transition-opacity duration-200' : '';
  const combinedImgClass = `${layout.imgClass} ${transitionClass} ${opacityClass} ${forwardedImgClassName || ''}`.trim();
  const thumbhashLayerClass = `absolute -inset-3 pointer-events-none bg-cover bg-center blur-md transition-opacity duration-200 ${
    showingThumbhash ? 'opacity-100' : 'opacity-0'
  }`;

  // Extract width-related styles for the parent div
  const divStyle = {};
  const stableFrameWidth = layout.finalFrameW;
  const isSingleRegular = layout.variant === 'single:regular';
  const isScrollerRegular = layout.variant?.startsWith('scroller:');
  if (isSingleRegular) {
    // Definite px width with a 100% cap (never width:100% + px max-width): percentage
    // widths make the frame's intrinsic contribution balloon to the image's natural
    // size, so the w-fit wrappers above stop hugging and push the hover rail far right.
    divStyle.width = stableFrameWidth;
    divStyle.maxWidth = '100%';
  } else if (isScrollerRegular) {
    divStyle.width = stableFrameWidth;
  } else {
    // Keep width deterministic on first paint while still responsive on narrow viewports.
    divStyle.width = stableFrameWidth;
    divStyle.maxWidth = '100%';
  }

  const baseFigureStyle = { ...layout.frameStyle };
  if (!isSingleRegular && !isScrollerRegular) {
    baseFigureStyle.width = stableFrameWidth;
    baseFigureStyle.maxWidth = '100%';
  }
  const imgStyle = isSingleRegular
    ? {
        ...layout.imgStyle,
        // Respect intrinsic aspect ratio when a caller supplies frame ratio overrides.
        width: '100%',
        height: 'auto',
        objectFit: layout.imgStyle?.objectFit || 'contain',
      }
    : layout.imgStyle;

  return (
    <div className={`inline-flex flex-col gap-1 ${isScrollerRegular ? 'shrink-0' : ''}`} style={divStyle}>
      <figure
        className={`group/image relative m-0 ${layout.frameClass} ${isSingleRegular || hasThumbhash ? 'overflow-hidden' : ''} ${className || ''}`}
        style={{
          ...baseFigureStyle,
          ...style,
        }}
        data-variant={layout.variant}
      >
        {hasThumbhash && (
          <div
            aria-hidden
            className={thumbhashLayerClass}
            style={{
              backgroundImage: `url(${thumbhashSrc})`,
            }}
          />
        )}

        {hasPreview && (
          <img
            src={previewSrc}
            alt=""
            aria-hidden
            draggable={false}
            className={`pointer-events-none absolute inset-0 m-auto ${layout.imgClass} ${showingPreview ? 'opacity-100' : 'opacity-0'}`}
            style={imgStyle}
            width={safeDimension(finalImgW ?? intrinsicDimensions.width, placeholderWidth)}
            height={safeDimension(finalImgH ?? intrinsicDimensions.height, placeholderHeight)}
          />
        )}

        {children && (
          <span className="absolute top-2 left-2 rounded-full bg-black/60 px-2 py-1 font-mono text-white opacity-0 shadow-[inset_0_0_0_0.5px_rgba(255,255,255,0.2)] backdrop-blur-[15px] backdrop-saturate-200 transition-opacity group-hover/image:opacity-100 has-[a:hover]:bg-black/40">
            {children}
          </span>
        )}

        <img
          key={displayedSrc}
          src={displayedSrc}
          alt={alt}
          draggable={false}
          className={combinedImgClass}
          style={imgStyle}
          width={intrinsicWidth}
          height={intrinsicHeight}
          onLoad={isDisplayingSource ? handleImageLoad : undefined}
          onError={isDisplayingSource ? handleImageError : undefined}
          {...restImgProps}
        />
      </figure>
    </div>
  );
}
